import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useDOA } from '../hooks/useDOA';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';

const API = `${API_V1}/contracts`;
const PAGE_SIZE = 50;

interface Obligation { id: number; obligation_type: string; description: string; due_date: string; amount: number | null; completion_pct: number; status: string; }
interface ContractItem { id: number; contract_number: string; contract_type: string; contract_subtype?: string | null; counterparty_id: string; title: string; total_value: number; committed_amount?: number | null; consumed_amount?: number | null; lifecycle_status: string; effective_date: string; expiration_date: string; auto_renewal: boolean; version: number; obligations: Obligation[]; entity_code?: string | null; }
interface Summary { total: number; active: number; draft: number; in_review: number; expired: number; total_active_value: number; expiring_90_days: number; by_type: Record<string, number>; }

const CONTRACT_TYPES = ['PURCHASE', 'SALES', 'SERVICE', 'LEASE', 'EMPLOYMENT', 'NDA', 'OTHER'];

const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', padding: '10px 14px', color: 'var(--text-hint)', letterSpacing: '0.04em', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { fontSize: 13, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' };

export default function ContractManagement() {
  const navigate = useNavigate();
  const { canApprove } = useDOA();
  const { entity } = useGlobalEntity();
  const [contracts, setContracts] = useState<ContractItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ contract_number: '', contract_type: 'SERVICE', counterparty_id: '', title: '', total_value: '', effective_date: '', expiration_date: '', auto_renewal: false });

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    if (typeFilter) p.set('contract_type', typeFilter);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setContracts(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) setSummary(await sumRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, typeFilter, entity]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const payload = { ...form, total_value: parseFloat(form.total_value) || 0 };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Contract created', true); setShowCreate(false); setForm({ contract_number: '', contract_type: 'SERVICE', counterparty_id: '', title: '', total_value: '', effective_date: '', expiration_date: '', auto_renewal: false }); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleAction = async (id: number, action: string, version: number) => {
    try {
      const res = await authFetch(`${API}/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: version }) });
      if (res.ok) { showToast(`Contract ${action.replace('-', ' ')}`, true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Action failed', false); }
  };

  const inputStyle: React.CSSProperties = { padding: '7px 12px', fontSize: 13, border: '1px solid var(--border-subtle)', borderRadius: 6, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' };

  return (
    <PageLayout
      title="Contract Management"
      subtitle="Create, review, and manage contracts with obligations and amendments"
      actions={[
        { label: '+ New Contract', onClick: () => setShowCreate(true) },
      ]}
    >
      {summary && (
        <KpiGrid columns={6} items={[
          { label: 'Total', value: summary.total },
          { label: 'Active', value: summary.active, color: 'var(--positive)' },
          { label: 'Draft', value: summary.draft },
          { label: 'In Review', value: summary.in_review, color: 'var(--warning)' },
          { label: 'Active Value', value: formatAmountCompact(summary.total_active_value), color: 'var(--accent)' },
          { label: 'Expiring (90d)', value: summary.expiring_90_days, color: summary.expiring_90_days > 0 ? 'var(--negative)' : undefined },
        ]} />
      )}

      <FilterBar filters={[
        { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, width: 180, options: ['DRAFT', 'IN_REVIEW', 'APPROVED', 'ACTIVE', 'AMENDED', 'EXPIRED', 'TERMINATED'].map(s => ({ value: s, label: s })) },
        { key: 'type', label: 'Type', type: 'select', value: typeFilter, onChange: v => { setTypeFilter(v); setPage(0); }, width: 180, options: CONTRACT_TYPES.map(t => ({ value: t, label: t })) },
      ]} />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : contracts.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No contracts found.</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['Contract #', 'Title', 'Type', 'Counterparty', 'Value', 'Expiry', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ ...thStyle, textAlign: h === 'Value' ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {contracts.map(c => (
                <>
                  <tr
                    key={c.id}
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                    style={{ cursor: 'pointer', background: expandedId === c.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent', transition: 'background 0.1s' }}
                    onMouseEnter={e => { if (expandedId !== c.id) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { if (expandedId !== c.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600 }} onClick={e => e.stopPropagation()}>
                      <a href={`/contracts/${c.id}`} onClick={e => { e.preventDefault(); navigate(`/contracts/${c.id}`); }} style={{ color: 'var(--accent)', textDecoration: 'none', cursor: 'pointer' }}>{c.contract_number}</a>
                    </td>
                    <td style={tdStyle}>{c.title || '-'}</td>
                    <td style={{ ...tdStyle, fontSize: 11 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>{c.contract_type}</span>
                        {c.contract_subtype && c.contract_subtype !== 'UNSPECIFIED' && (
                          <span
                            style={{
                              display: 'inline-block',
                              alignSelf: 'flex-start',
                              fontSize: 9,
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: 4,
                              background: c.contract_subtype === 'MSA_VALUE' ? 'rgba(139, 92, 246, 0.12)' : 'rgba(100, 116, 139, 0.12)',
                              color: c.contract_subtype === 'MSA_VALUE' ? 'rgb(139, 92, 246)' : 'var(--text-secondary)',
                              textTransform: 'uppercase' as const,
                              letterSpacing: '0.04em',
                            }}
                          >
                            {c.contract_subtype.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{c.counterparty_id || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(c.total_value)}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 12 }}>{c.expiration_date || '-'}{c.auto_renewal && <span style={{ marginLeft: 4, fontSize: 9, color: 'var(--warning)' }}>AUTO</span>}</td>
                    <td style={tdStyle}><StatusBadge status={c.lifecycle_status} /></td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {c.lifecycle_status === 'DRAFT' && <ActionButton label="Review" onClick={() => handleAction(c.id, 'submit-review', c.version)} variant="secondary" size="sm" />}
                        {c.lifecycle_status === 'IN_REVIEW' && <ActionButton label="Approve" onClick={() => handleAction(c.id, 'approve', c.version)} variant="success" size="sm" disabled={!canApprove(c.total_value || 0, c.entity_code, 'CONTRACT')} title={!canApprove(c.total_value || 0, c.entity_code, 'CONTRACT') ? 'Exceeds your approval authority for this entity / category' : undefined} />}
                        {c.lifecycle_status === 'APPROVED' && <ActionButton label="Activate" onClick={() => handleAction(c.id, 'activate', c.version)} variant="primary" size="sm" />}
                        {c.lifecycle_status === 'ACTIVE' && <ActionButton label="Terminate" onClick={() => handleAction(c.id, 'terminate', c.version)} variant="danger" size="sm" />}
                      </div>
                    </td>
                  </tr>
                  {expandedId === c.id && c.obligations && c.obligations.length > 0 && (
                    <tr key={`${c.id}-obs`}><td colSpan={8} style={{ padding: '0 14px 12px 32px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 8, marginBottom: 4 }}>Obligations</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          {['Type', 'Description', 'Due Date', 'Amount', 'Progress', 'Status'].map(h => (
                            <th key={h} style={{ ...thStyle, fontSize: 11, padding: '6px 14px' }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>{c.obligations.map(ob => (
                          <tr key={ob.id}>
                            <td style={{ ...tdStyle, fontWeight: 600, fontSize: 11 }}>{ob.obligation_type}</td>
                            <td style={tdStyle}>{ob.description || '-'}</td>
                            <td style={tdStyle}>{ob.due_date || '-'}</td>
                            <td style={tdStyle}>{ob.amount != null ? formatAmount(ob.amount) : '-'}</td>
                            <td style={tdStyle}>{ob.completion_pct}%</td>
                            <td style={tdStyle}><StatusBadge status={ob.status} /></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </td></tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Contract" size="lg" footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create" onClick={handleCreate} variant="primary" /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Contract Number *" value={form.contract_number} onChange={e => setForm({ ...form, contract_number: e.target.value })} style={inputStyle} />
            <select value={form.contract_type} onChange={e => setForm({ ...form, contract_type: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
              {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <input placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={inputStyle} />
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Counterparty ID" value={form.counterparty_id} onChange={e => setForm({ ...form, counterparty_id: e.target.value })} style={inputStyle} />
            <input placeholder="Total Value" type="number" value={form.total_value} onChange={e => setForm({ ...form, total_value: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: 'var(--text-hint)' }}>Effective Date</label><input type="date" value={form.effective_date} onChange={e => setForm({ ...form, effective_date: e.target.value })} style={inputStyle} /></div>
            <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: 'var(--text-hint)' }}>Expiration Date</label><input type="date" value={form.expiration_date} onChange={e => setForm({ ...form, expiration_date: e.target.value })} style={inputStyle} /></div>
          </div>
          <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={form.auto_renewal} onChange={e => setForm({ ...form, auto_renewal: e.target.checked })} /> Auto-renewal
          </label>
        </div>
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
